import { BubbleProps } from "@ant-design/x";
import { Typography, Button, Space } from "antd";
import { LikeOutlined, DislikeOutlined } from "@ant-design/icons";
import markdownit from "markdown-it";
import React from "react";
import type Token from "markdown-it/lib/token.mjs";

// 初始化 markdown 实例，启用 linkify
const markdown = markdownit({ html: true, breaks: true, linkify: true });

// 自定义渲染规则：所有链接默认在新标签页打开
const defaultLinkOpen =
  markdown.renderer.rules.link_open ||
  ((tokens: Token[], idx: number, options, env, self) =>
    self.renderToken(tokens, idx, options));

markdown.renderer.rules.link_open = (tokens, idx, options, env, self) => {
  const token = tokens[idx];
  token.attrSet("target", "_blank");
  token.attrSet("rel", "noopener noreferrer");
  return defaultLinkOpen(tokens, idx, options, env, self);
};
// AI响应接口
export interface AIResponse {
  Answer: string;
  Suggestion: {
    Q1: string;
    Q2: string;
    Q3: string;
  };
}

// 渲染markdown方法
export const renderMarkdown: BubbleProps["messageRender"] = (content) => {
  // 确保 content 是字符串类型
  const stringContent =
    typeof content === "string" ? content : String(content || "");

  // 预处理：将 \n 转换为双换行符，确保 markdown 能正确识别段落
  let processedContent = stringContent
    .replace(/\\n/g, "\n") // 将字符串中的 \n 转换为真正的换行符
    .replace(/\n\n+/g, "\n\n"); // 将多个连续换行符规范化为双换行符

  // 处理包裹在 [] 内但缺少 () 的链接，例如：[https://example.com]
  processedContent = processedContent.replace(
    /\[(https?:\/\/[^\s\]]+)\]/g,
    (_, url: string) => `[${url}](${url})`
  );

  // 处理裸露的 URL，将其转换为 Markdown 链接
  processedContent = processedContent.replace(
    /(^|\s)(https?:\/\/[^\s]+)/g,
    (_, prefix: string, url: string) => `${prefix}[${url}](${url})`
  );

  const handleLinkClick = (event: React.MouseEvent<HTMLElement>) => {
    const target = event.target as HTMLElement | null;
    if (!target) return;

    const anchor = target.closest("a");
    if (anchor instanceof HTMLAnchorElement) {
      event.preventDefault();
      const href = anchor.href;
      if (href) {
        window.open(href, "_blank", "noopener,noreferrer");
      }
    }
  };

  return (
    <Typography onClick={handleLinkClick}>
      {/* biome-ignore lint/security/noDangerouslySetInnerHtml: used in demo */}
      <div
        dangerouslySetInnerHTML={{ __html: markdown.render(processedContent) }}
      />
    </Typography>
  );
};

// 渲染图片、视频和文字的方法
export const renderLocalMessage: BubbleProps["messageRender"] = (content) => {
  // 如果content不是字符串，直接返回content（可能是React组件）
  if (typeof content !== "string") {
    return content;
  }

  if (isImageUrl(content)) {
    return (
      <div
        style={{
          cursor: "pointer",
          maxWidth: "200px",
          marginTop: "8px",
          overflow: "hidden",
          borderRadius: "4px",
        }}
        onClick={() => {
          // 触发自定义事件，用于图片预览
          const event = new CustomEvent("preview-image", {
            detail: { url: content },
          });
          window.dispatchEvent(event);
        }}
      >
        <img
          src={content}
          alt="message"
          style={{
            width: "100%",
            height: "auto",
            maxHeight: "200px",
            objectFit: "contain",
            borderRadius: "4px",
            willChange: "transform",
            backfaceVisibility: "hidden",
            transform: "translateZ(0)",
            cursor: "pointer",
            transition: "opacity 0.2s",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.opacity = "0.9";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.opacity = "1";
          }}
        />
      </div>
    );
  }

  if (isVideoUrl(content)) {
    return (
      <div
        style={{
          cursor: "pointer",
          maxWidth: "200px",
          marginTop: "8px",
          overflow: "hidden",
          borderRadius: "4px",
          position: "relative",
        }}
        onClick={() => {
          // 触发自定义事件，用于视频预览
          const event = new CustomEvent("preview-video", {
            detail: { url: content },
          });
          window.dispatchEvent(event);
        }}
      >
        <video
          src={content}
          style={{
            width: "100%",
            height: "auto",
            maxHeight: "200px",
            objectFit: "contain",
            borderRadius: "4px",
            cursor: "pointer",
          }}
          preload="metadata"
        />
        <div
          style={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: "48px",
            height: "48px",
            backgroundColor: "rgba(0, 0, 0, 0.6)",
            borderRadius: "50%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "white",
            fontSize: "20px",
            pointerEvents: "none",
          }}
        >
          ▶
        </div>
      </div>
    );
  }

  // 检查是否为PDF文件URL（需要在 isHtmlUrl 之前检查，避免PDF查看器页面被误判为HTML）
  if (isPdfUrl(content)) {
    return (
      <div
        style={{
          cursor: "pointer",
          maxWidth: "250px",
          marginTop: "8px",
          overflow: "hidden",
          borderRadius: "4px",
          position: "relative",
        }}
        onClick={() => {
          // 触发自定义事件，用于文件预览
          const event = new CustomEvent("preview-file", {
            detail: { url: content },
          });
          window.dispatchEvent(event);
        }}
      >
        <div
          style={{
            position: "relative",
            width: "100%",
            height: "200px",
            backgroundColor: "#ffffff",
            overflow: "hidden",
            borderRadius: "4px",
            willChange: "transform",
            backfaceVisibility: "hidden",
            transform: "translateZ(0)",
            transition: "opacity 0.2s",
            border: "1px solid #f0f0f0",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.opacity = "0.9";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.opacity = "1";
          }}
        >
          <iframe
            src={`${content}#page=1&zoom=page-width`}
            style={{
              border: "none",
              pointerEvents: "none",
              transform: "scale(0.3)",
              transformOrigin: "top left",
              width: "333%",
              height: "333%",
            }}
            title="PDF Preview"
            loading="lazy"
          />
        </div>
      </div>
    );
  }

  if (isHtmlUrl(content)) {
    return (
      <div
        style={{
          maxWidth: "100%",
          marginTop: "8px",
        }}
      >
        <HtmlContentRenderer url={content} />
      </div>
    );
  }

  return renderMarkdown(content);
};

// 判断内容是否为图片URL的函数
export const isImageUrl = (content: string) => {
  return content.match(/^https?:\/\/.*\.(jpg|jpeg|png|gif|webp)(\?.*)?$/i);
};

// 判断内容是否为视频URL的函数
export const isVideoUrl = (content: string) => {
  return content.match(/^https?:\/\/.*\.(mp4|webm|mov|avi|mkv)(\?.*)?$/i);
};

// 判断内容是否为PDF文件URL的函数
export const isPdfUrl = (content: string) => {
  const trimmed = (content || "").trim();
  // 仅当整段内容本身是 URL 时才按 PDF 处理
  if (!/^https?:\/\/\S+$/i.test(trimmed)) {
    return false;
  }
  // 匹配以 pdf 结尾的PDF文件链接
  if (/\pdf(\?|#|$)/i.test(trimmed)) {
    return true;
  }
  // 匹配PDF查看器URL（包含 pdf_viewer.html 或 pdf= 参数）
  if (/pdf_viewer\.html/i.test(trimmed) || /[?&]pdf=/i.test(trimmed)) {
    return true;
  }
  return false;
};

// 判断内容是否为HTML页面URL的函数
export const isHtmlUrl = (content: string) => {
  const trimmed = (content || "").trim();

  // 仅当整段内容本身是 URL 时才按 HTML 链接处理
  if (!/^https?:\/\/\S+$/i.test(trimmed)) {
    return false;
  }

  // paypz.masayapay.com 域名需要特殊处理
  if (trimmed.includes("paypz.masayapay.com")) {
    return true;
  }

  // 仅匹配以 .html 或 .htm 结尾的网页链接
  return /\.html?(\?|#|$)/i.test(trimmed);
};

// 获取HTML页面内容
export const fetchHtmlContent = async (url: string): Promise<string | null> => {
  try {
    console.log("Attempting to fetch HTML content from:", url);

    const response = await fetch(url, {
      mode: "cors",
      headers: {
        Accept:
          "text/html,application/xhtml+xml,application/xml;q=0.9,*/*;q=0.8",
        "User-Agent": "Mozilla/5.0 (compatible; ChatBot/1.0)",
      },
    });

    console.log("Response status:", response.status);
    console.log("Response headers:", response.headers);

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const html = await response.text();
    console.log("Successfully fetched HTML content, length:", html.length);
    return html;
  } catch (error) {
    console.error("Error fetching HTML content:", error);
    if (error instanceof Error) {
      console.error("Error details:", {
        name: error.name,
        message: error.message,
        stack: error.stack,
      });
    }
    return null;
  }
};

// 处理问题字符串
export const processQuestion = (question: string): string[] => {
  try {
    // 尝试解析 JSON 字符串
    const parsed = JSON.parse(question);
    if (Array.isArray(parsed)) {
      return parsed;
    }
    return [question];
  } catch (error) {
    // 如果解析失败，返回原始字符串作为数组
    return [question];
  }
};

// 处理AI响应
export const processAIResponse = (model: string, response: string): string => {
  let answer = "";
  try {
    const responseJson = JSON.parse(response) as AIResponse;
    if (model === "deepseek-r1:14b") {
      answer = responseJson.Answer.replace(/<think>[\s\S]*?<\/think>/g, "");
    } else {
      answer = responseJson.Answer;
    }
  } catch {
    answer = response;
  }
  return markdown.render(answer);
};

// 评分按钮组件
export const RatingButtons: React.FC<{
  uuid: string;
  rating?: number;
  onRate: (uuid: string, rating: number) => void;
}> = ({ uuid, rating, onRate }) => {
  const currentRating = rating || 0;
  const isLiked = currentRating === 1;
  const isDisliked = currentRating === 2;

  return (
    <Space size={4} style={{ marginTop: "8px" }}>
      <Button
        type="text"
        size="small"
        icon={<LikeOutlined />}
        onClick={() => onRate(uuid, 1)}
        style={{
          color: isLiked ? "#52c41a" : "#8c8c8c",
          padding: "2px 6px",
          height: "24px",
          fontSize: "12px",
        }}
      />
      <Button
        type="text"
        size="small"
        icon={<DislikeOutlined />}
        onClick={() => onRate(uuid, 2)}
        style={{
          color: isDisliked ? "#ff4d4f" : "#8c8c8c",
          padding: "2px 6px",
          height: "24px",
          fontSize: "12px",
        }}
      />
    </Space>
  );
};

// 渲染HTML内容组件
const HtmlContentRenderer = ({ url }: { url: string }) => {
  const [htmlContent, setHtmlContent] = React.useState<string | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [useIframe, setUseIframe] = React.useState(false);

  React.useEffect(() => {
    const loadHtml = async () => {
      setLoading(true);
      setError(null);

      const content = await fetchHtmlContent(url);
      if (content) {
        setHtmlContent(content);
      } else {
        setError(
          "Unable to load page content. This might be due to CORS restrictions or network issues."
        );
      }
      setLoading(false);
    };

    loadHtml();
  }, [url]);

  // 如果fetch失败，自动使用iframe作为备用方案
  React.useEffect(() => {
    if (error && !useIframe) {
      setUseIframe(true);
    }
  }, [error, useIframe]);

  if (loading) {
    return (
      <div
        style={{
          width: "100%",
          height: "200px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: "#f5f5f5",
          borderRadius: "8px",
          border: "1px solid #e8e8e8",
        }}
      >
        <Typography.Text type="secondary">Loading page...</Typography.Text>
      </div>
    );
  }

  // 如果fetch失败，使用iframe作为备用方案
  if (useIframe) {
    return (
      <div
        style={{
          cursor: "pointer",
          maxWidth: "100%",
          marginTop: "8px",
          overflow: "hidden",
          borderRadius: "8px",
          border: "1px solid #e8e8e8",
          backgroundColor: "#fff",
          position: "relative",
          boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
        }}
        onClick={() => {
          // 在新窗口中打开HTML页面
          window.open(url, "_blank", "noopener,noreferrer");
        }}
      >
        <iframe
          src={url}
          style={{
            width: "100%",
            height: "400px",
            border: "none",
            borderRadius: "8px",
            pointerEvents: "none",
            willChange: "transform",
            backfaceVisibility: "hidden",
            transform: "translateZ(0)",
          }}
          title="HTML Page"
          sandbox="allow-same-origin allow-scripts"
          onMouseEnter={(e) => {
            e.currentTarget.style.opacity = "0.9";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.opacity = "1";
          }}
        />
        {/* 添加点击提示 */}
        <div
          style={{
            position: "absolute",
            top: "8px",
            right: "8px",
            fontSize: "12px",
            color: "rgba(0, 0, 0, 0.6)",
            backgroundColor: "rgba(255, 255, 255, 0.95)",
            padding: "4px 8px",
            borderRadius: "12px",
            border: "1px solid rgba(0, 0, 0, 0.1)",
            pointerEvents: "none",
            fontWeight: 500,
          }}
        >
          Click to open
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div
        style={{
          width: "100%",
          padding: "16px",
          backgroundColor: "#fff2f0",
          borderRadius: "8px",
          border: "1px solid #ffccc7",
        }}
      >
        <Typography.Text
          type="danger"
          style={{ display: "block", marginBottom: "12px" }}
        >
          Failed to load page
        </Typography.Text>
        <Typography.Text
          type="secondary"
          style={{ display: "block", marginBottom: "12px", fontSize: "12px" }}
        >
          {error}
        </Typography.Text>
        <div style={{ display: "flex", gap: "8px" }}>
          <Typography.Link
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            style={{ fontSize: "14px" }}
          >
            Open in new tab
          </Typography.Link>
          <Typography.Link
            onClick={() => {
              setUseIframe(true);
            }}
            style={{ fontSize: "14px" }}
          >
            Use iframe preview
          </Typography.Link>
          <Typography.Link
            onClick={() => {
              setLoading(true);
              setError(null);
              setUseIframe(false);
              const loadHtml = async () => {
                const content = await fetchHtmlContent(url);
                if (content) {
                  setHtmlContent(content);
                } else {
                  setError("Still unable to load page content.");
                }
                setLoading(false);
              };
              loadHtml();
            }}
            style={{ fontSize: "14px" }}
          >
            Retry
          </Typography.Link>
        </div>
      </div>
    );
  }

  return (
    <div
      style={{
        maxWidth: "100%",
        maxHeight: "500px",
        overflow: "auto",
        border: "1px solid #e8e8e8",
        borderRadius: "8px",
        backgroundColor: "#fff",
        boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
      }}
    >
      {/* biome-ignore lint/security/noDangerouslySetInnerHtml: used for HTML content rendering */}
      <div dangerouslySetInnerHTML={{ __html: htmlContent || "" }} />
    </div>
  );
};
